import { macroEnergyPercentage } from '@/utils/macro-energy';
import { resolveClientTargetCalories } from '@/utils/client-goals';
import { formatStructuredClinicalLimit } from './analysis/clinicalTargetSemantics';
import { formatHeight, formatWeight } from '@/utils/unit-conversion';
import type { Biomarker, Client, ClientActiveSubscription } from '@/types/client';
import { formatDate } from '@/utils/date-utils';
import { endOfDay, startOfDay } from 'date-fns';
import { getValidAllergies, getValidFoodIntolerances, getValidPreferences } from '@/lib/clientPreferenceOptions';
import { formatDisplayLabel } from '@/utils/display-labels';
import { getActiveMedicalConditionLabels } from '@/utils/client-medical-conditions';

export type GoalItem = {
  label: string;
  subtitle: string;
  progress: number;
};

export type MetricItem = {
  label: string;
  value: string;
};

export type DetailMetricItem = {
  label: string;
  value: string;
};

export type ClientGoalRow = {
  nutrient: string;
  percentage: string;
  referenceRange: string;
  grams: string;
  kcal?: string;
  unit?: string;
};

export type SummaryItem = {
  title: string;
  value: string;
  valueParts?: string[];
};

export type ActiveSubscriptionSummary = {
  periodLabel: string;
  servicesLabel: string;
  statusLabel: string;
};

export type AboutDetails = {
  fullName: string;
  dateOfBirth: string;
  gender: string;
  sexAtBirth: string;
  phone: string;
  email: string;
  location: string;
  cityOrCounty: string;
  occupation: string;
  emergencyContact: string;
  pregnancyStatus?: string;
  pregnancyTrimester?: string;
  pregnancyType?: string;
};

export const EMPTY_CLIENT_DETAIL_VALUE = '-';

export interface ClientDetailsV2ViewModel {
  clientDisplayName: string;
  emailDisplay: string;
  clientsBackPath: string;
  nextAppointmentDate: string | null;
  statusLabel: string;
  aboutDetails: AboutDetails;
  allergies: string[];
  intolerances: string[];
  summaryItems: SummaryItem[];
  metrics: MetricItem[];
  anthropometricItems: DetailMetricItem[];
  activityAndGoalItems: DetailMetricItem[];
  symptomItems: DetailMetricItem[];
  dietaryPreferencesSummary: string;
  clientGoalSummary: string;
  nutrientGuidelinesSummary: string;
  clientGoalRows: ClientGoalRow[];
  goals: GoalItem[];
  conditionNames: string[];
  biomarkerItems: Biomarker[];
  completedQuestionnaires: number;
  questionnaireCompletedLabel: string;
  questionnaireProgressLabel: string;
  activeSubscriptionSummary: ActiveSubscriptionSummary | null;
}

export const tabs = [
  'Overview',
  'Appointments',
  'Suggested RDA',
  'Client Targets',
  'Analysis',
  'Meal Plan',
  'Lab Reports',
  'Questionnaires',
  'Invoices',
  'Notes',
] as const;

export const tabRouteSegments = [
  'overview',
  'appointments',
  'suggested-rda',
  'client-targets',
  'analysis',
  'meal-plan',
  'lab-reports',
  'questionnaires',
  'invoices',
  'flags-and-notes',
] as const;

export function getClientDetailsV2TabIndexFromRouteSegment(routeSegment?: string | null): number {
  if (!routeSegment) return 0;
  const normalizedSegment = routeSegment.trim().toLowerCase();
  const index = tabRouteSegments.findIndex((segment) => segment === normalizedSegment);
  return index >= 0 ? index : 0;
}

export function getClientDetailsV2TabRouteSegmentByIndex(index: number): string {
  return tabRouteSegments[index] ?? tabRouteSegments[0];
}

const NON_ACTIVE_CLIENT_STATUSES = new Set([
  'inactive',
  'suspended',
  'archived',
]);

export function shouldShowClientDataRetentionWarning(status: string | undefined | null): boolean {
  const normalized = String(status ?? '').trim().toLowerCase();
  return NON_ACTIVE_CLIENT_STATUSES.has(normalized);
}

/** Appointment window that is happening now and overlaps the local calendar day. */
export type OngoingVisitWindow = { start: Date; end: Date };

/**
 * Picks an in-person visit that overlaps "today" (local) and where now ∈ [start, end).
 * Used for the client header strip; returns null when nothing is in progress.
 */
export function findOngoingVisitOverlappingToday(appointments: unknown[]): OngoingVisitWindow | null {
  if (!Array.isArray(appointments) || appointments.length === 0) return null;
  const now = Date.now();
  const todayStart = startOfDay(new Date()).getTime();
  const todayEnd = endOfDay(new Date()).getTime();

  for (const item of appointments) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as Record<string, unknown>;
    const status = String(raw.status ?? '').toLowerCase();
    if (status === 'cancelled' || status === 'completed') continue;

    const appointmentType = String(raw.appointmentType ?? raw.mode ?? '').toLowerCase();
    const location = String(raw.location ?? '').toLowerCase();
    const isOfflineVisit =
      appointmentType === 'offline' ||
      appointmentType === 'in_person' ||
      location.includes('person');
    if (!isOfflineVisit) continue;

    const startSrc = raw.startTime ?? raw.start_time ?? raw.date;
    const start = startSrc instanceof Date ? startSrc : new Date(String(startSrc));
    if (Number.isNaN(start.getTime())) continue;

    const endSrc = raw.endTime ?? raw.end_time;
    const durationRaw = Number(raw.duration ?? raw.durationMinutes);
    const durationMin = Number.isFinite(durationRaw) && durationRaw > 0 ? durationRaw : 60;
    const end = endSrc
      ? endSrc instanceof Date
        ? endSrc
        : new Date(String(endSrc))
      : new Date(start.getTime() + durationMin * 60_000);
    if (Number.isNaN(end.getTime())) continue;

    if (end.getTime() < todayStart || start.getTime() > todayEnd) continue;
    if (now < start.getTime() || now >= end.getTime()) continue;

    return { start, end };
  }

  return null;
}

export const panelTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: 'clamp(0.8125rem, 1vw, 0.9375rem)',
  lineHeight: 1.25,
  color: '#0f172a',
} as const;

export const tabPanelSx = {
  mt: 2.25,
  borderRadius: '16px',
  border: '1px solid #dbe6eb',
  p: { xs: 1.75, sm: 2, md: 2.5 },
  bgcolor: '#fff',
} as const;

export const blueSectionHeadingFontSize = '0.75rem';
const EMPTY_VALUE_DISPLAY = '-';

const bmrFormulaLabels: Record<string, string> = {
  henry_oxford: 'Henry Oxford',
  schofield: 'Schofield',
  mifflin_st_jeor: 'Mifflin-St Jeor (1990)',
  iom: 'IOM',
  icmr: 'ICMR-NIN (2010)',
  harris_benedict_1984: 'Harris-Benedict (1984)',
  harris_benedict_1919: 'Harris-Benedict (1919)',
};

const drvSourceLabels: Record<string, string> = {
  icmr_nin: 'ICMR-NIN',
  icmr_india: 'ICMR India',
  iom_usa: 'IOM USA',
  efsa_eu: 'EFSA EU',
  who_fao: 'WHO FAO',
  uk_sacn: 'UK SACN',
  ausnz_nrv: 'AUS/NZ NRV',
};

export function toTitleCase(value: string): string {
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatLactationStatus(value?: string | null): string {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized || normalized === 'none') return EMPTY_VALUE_DISPLAY;

  return /^\d{1,2}-\d{1,2}$/.test(normalized)
    ? `${normalized} Months`
    : toTitleCase(normalized);
}

function calculateIdealWeightKg(heightCm?: number | null, bmi?: number | null): number | null {
  const height = Number(heightCm);
  const targetBmi = Number(bmi);
  if (!Number.isFinite(height) || height <= 0 || !Number.isFinite(targetBmi) || targetBmi <= 0) return null;

  const heightMeters = height / 100;
  const idealWeight = targetBmi * heightMeters * heightMeters;
  return Math.round(idealWeight * 10) / 10;
}

function getAge(dateOfBirth?: string | null): number | null {
  if (!dateOfBirth) return null;

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function getMedicalConditionNames(client: Client): string[] {
  return getActiveMedicalConditionLabels(client);
}

function formatListPreview(values: string[], maxVisible = 3): string {
  if (!values.length) return EMPTY_CLIENT_DETAIL_VALUE;
  if (values.length <= maxVisible) return values.join(', ');
  return `${values.slice(0, maxVisible).join(', ')} +${values.length - maxVisible} more`;
}

function formatPhoneDisplay(phoneCountryCode?: string | null, phone?: string | null): string {
  const code = (phoneCountryCode || '').trim();
  const value = (phone || '').trim();
  return [code, value].filter(Boolean).join(' ') || EMPTY_CLIENT_DETAIL_VALUE;
}

function getPregnancyDetails(client: Client): {
  pregnancyStatus?: string;
  pregnancyTrimester?: string;
  pregnancyType?: string;
} {
  const rawValue = String(client.pregnancyTrimester ?? client.pregnancyStatus ?? '').trim().toLowerCase();
  const normalized = rawValue.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  const compact = normalized.replace(/\s+/g, '');

  if (!normalized) return {};
  if (['none', 'notpregnant', 'nonpregnant'].includes(compact)) {
    return {};
  }

  let trimester: string | undefined;
  if (normalized.includes('first') || normalized.includes('1st') || normalized === '1') {
    trimester = 'First Trimester';
  } else if (normalized.includes('second') || normalized.includes('2nd') || normalized === '2') {
    trimester = 'Second Trimester';
  } else if (normalized.includes('third') || normalized.includes('3rd') || normalized === '3') {
    trimester = 'Third Trimester';
  }

  const isPregnant = normalized.includes('pregnant') || Boolean(trimester);
  if (!isPregnant) return {};

  return {
    pregnancyStatus: 'Pregnant',
    pregnancyTrimester: trimester || EMPTY_VALUE_DISPLAY,
    pregnancyType: client.isTwinPregnancy ? 'Twin Pregnancy' : 'Single Pregnancy',
  };
}

export function isPregnantFemaleClient(client?: Client | null): boolean {
  if (!client) return false;

  const gender = String(client.gender || '').trim().toLowerCase();
  const sexAtBirth = String(client.sexAtBirth || '').trim().toLowerCase();
  const effectiveSex = gender === 'male' || gender === 'female' ? gender : sexAtBirth;
  const isPregnancyValue = (value: unknown): boolean => {
    const normalized = String(value ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    const compact = normalized.replace(/\s+/g, '');

    if (!normalized || ['none', 'notpregnant', 'nonpregnant'].includes(compact)) {
      return false;
    }

    return (
      normalized.includes('pregnant') ||
      normalized.includes('first') ||
      normalized.includes('1st') ||
      normalized === '1' ||
      normalized.includes('second') ||
      normalized.includes('2nd') ||
      normalized === '2' ||
      normalized.includes('third') ||
      normalized.includes('3rd') ||
      normalized === '3'
    );
  };

  return effectiveSex === 'female' && (isPregnancyValue(client.pregnancyTrimester) || isPregnancyValue(client.pregnancyStatus));
}

function formatCircumference(cmValue: number | null | undefined, measurementSystem: 'metric' | 'imperial'): string {
  const cm = Number(cmValue);
  if (!Number.isFinite(cm) || cm <= 0) return 'Not set';

  if (measurementSystem === 'imperial') {
    return `${(cm / 2.54).toFixed(1)} in`;
  }

  return `${cm.toFixed(1)} cm`;
}

function toNullableFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

type UnknownRecord = Record<string, unknown>;

function toUnknownRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function getGoals(client: Client): GoalItem[] {
  if (Array.isArray(client.healthGoals) && client.healthGoals.length) {
    return client.healthGoals.map((goal, index) => ({
      label: toTitleCase(goal),
      subtitle: index % 2 === 0 ? 'Improvement target' : 'Lifestyle target',
      progress: Math.min(85, 25 + index * 15),
    }));
  }

  return [
    { label: 'Weight Management', subtitle: 'Default target', progress: 32 },
    { label: 'Blood Sugar', subtitle: 'Default target', progress: 40 },
    { label: 'Energy Levels', subtitle: 'Default target', progress: 48 },
  ];
}

export function getBiomarkerColor(status?: string): { bg: string; text: string; border: string } {
  const normalized = String(status ?? '').toLowerCase();

  if (normalized === 'normal') {
    return { bg: '#ebfdf2', text: '#027a48', border: '#b7efd2' };
  }

  if (normalized === 'high' || normalized === 'critical') {
    return { bg: '#fff1f3', text: '#b42318', border: '#fecdd3' };
  }

  if (normalized === 'low') {
    return { bg: '#fff8eb', text: '#b54708', border: '#fcdca8' };
  }

  if (normalized === 'borderline') {
    return { bg: '#eff8ff', text: '#175cd3', border: '#b2ddff' };
  }

  return { bg: '#f6f8fb', text: '#344054', border: '#d0d5dd' };
}

function getMetricItems(client: Client): MetricItem[] {
  const measurementSystem = (client.preferredMeasurementSystem || 'metric') as 'metric' | 'imperial';
  const shouldHideBmiInfo = isPregnantFemaleClient(client);

  const items: MetricItem[] = [
    {
      label: 'Weight',
      value: client.weightKg ? formatWeight(client.weightKg, measurementSystem) : 'Not set',
    },
    {
      label: 'Height',
      value: client.heightCm ? formatHeight(client.heightCm, measurementSystem) : 'Not set',
    },
  ];

  if (!shouldHideBmiInfo) {
    items.push({
      label: 'BMI',
      value: Number.isFinite(Number(client.bmi)) ? `${Number(client.bmi).toFixed(1)}` : 'Not set',
    });
  }

  return items;
}

function getNutrientGuidelinesLabel(client: Client): string {
  const formulaId = String(client.bmrFormulaId || (client as Client & { formulaId?: string | null }).formulaId || '').trim();
  if (formulaId) return bmrFormulaLabels[formulaId] || toTitleCase(formulaId);

  const formulaDisplayName = client.calculationDetails?.formulaDisplayName?.trim();
  if (formulaDisplayName) return formulaDisplayName;

  return 'Not set';
}

function getDrvSourceLabel(client: Client): string {
  const drvGuidelineId = client.drvGuidelineId?.trim() || client.drvFormulaId?.trim();
  if (drvGuidelineId) return drvSourceLabels[drvGuidelineId] || toTitleCase(drvGuidelineId);

  const drvSource = client.drvSource?.trim();
  if (!drvSource) return 'Not set';

  return drvSourceLabels[drvSource] || toTitleCase(drvSource);
}

function getNutrientGuidelinesSummary(client: Client): string {
  const sourceLabel = getDrvSourceLabel(client);
  return sourceLabel === 'Not set' ? sourceLabel : `DRV Formula- ${sourceLabel}`;
}

function readGoalCalories(clientGoals: unknown): number | null {
  const goals = toUnknownRecord(clientGoals);
  if (!goals) return null;
  const direct = Number(goals.eerGoalCalories ?? goals.targetCalories);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const nested = toUnknownRecord(goals.calories) ?? toUnknownRecord(goals.dailyEnergyExpenditure);
  if (!nested) return null;
  const nestedKcal = toUnknownRecord(nested.kcal);
  const nestedValue = Number(nested.value ?? nestedKcal?.value);
  return Number.isFinite(nestedValue) && nestedValue > 0 ? nestedValue : null;
}

function firstPositiveNumber(...values: unknown[]): number | null {
  for (const value of values) {
    const parsed = toNullableFiniteNumber(value);
    if (parsed != null && parsed > 0) return parsed;
  }
  return null;
}

function readStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((item) => String(item).trim()).filter(Boolean)
    : [];
}

function normalizeClinicalConditionLabel(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return '';
  if (/\bt2d\b|type\s*2|type\s*ii/.test(normalized)) return 'Type 2 Diabetes (T2D)';
  return formatDisplayLabel(value);
}

function readAppliedEnergyRule(value: unknown): string | undefined {
  return readStringList(value).find((rule) => /^energy:/i.test(rule));
}

export type ClientClinicalCalorieAdjustmentInfo = {
  originalCalories: number;
  adjustedCalories: number;
  reductionCalories: number;
  conditionLabel: string;
  bmi: number | null;
  appliedRule?: string;
};

export function getClientClinicalCalorieAdjustmentInfo(client: Client): ClientClinicalCalorieAdjustmentInfo | null {
  const source = client as Client & {
    conditionAdjustedTargetCalories?: number | null;
    finalTargetCalories?: number | null;
    maintenanceEER?: number | null;
    targetCalories?: number | null;
  };
  const goals = toUnknownRecord(client.clientGoals);
  const clinicalRules = toUnknownRecord(goals?.clinicalRules);
  const resolvedTargets = toUnknownRecord(clinicalRules?.resolvedTargets);

  const originalCalories = firstPositiveNumber(
    resolvedTargets?.maintenanceEER,
    source.maintenanceEER,
    client.eerCalories,
    client.eer,
  );
  const adjustedCalories = firstPositiveNumber(
    resolvedTargets?.finalTargetCalories,
    resolvedTargets?.targetCalories,
    resolvedTargets?.conditionAdjustedTargetCalories,
    source.conditionAdjustedTargetCalories,
    source.finalTargetCalories,
    source.targetCalories,
  );

  if (originalCalories == null || adjustedCalories == null) return null;
  const reductionCalories = Math.round(originalCalories - adjustedCalories);
  if (reductionCalories <= 0) return null;

  const matchedConditionLabel = readStringList(clinicalRules?.matchedConditions)
    .map(normalizeClinicalConditionLabel)
    .find(Boolean);
  const activeConditionLabel = getActiveMedicalConditionLabels(client)
    .map(normalizeClinicalConditionLabel)
    .find(Boolean);
  const bmi = toNullableFiniteNumber(client.bmi);

  return {
    originalCalories: Math.round(originalCalories),
    adjustedCalories: Math.round(adjustedCalories),
    reductionCalories,
    conditionLabel: matchedConditionLabel || activeConditionLabel || 'Clinical rule',
    bmi: bmi != null && bmi > 0 ? bmi : null,
    appliedRule: readAppliedEnergyRule(clinicalRules?.appliedRules),
  };
}

export function getClientDailyCalorieGoalKcal(client: Client): number | null {
  const source = client as Client & {
    conditionAdjustedTargetCalories?: number | null;
    eerGoalCalories?: number | null;
    finalTargetCalories?: number | null;
    maintenanceEER?: number | null;
    targetCalories?: number | null;
  };
  const clinicalAdjustment = getClientClinicalCalorieAdjustmentInfo(client);
  const candidates = [
    clinicalAdjustment?.adjustedCalories,
    source.finalTargetCalories,
    source.targetCalories,
    source.conditionAdjustedTargetCalories,
    readGoalCalories(client.clientGoals),
    source.eerGoalCalories,
    client.eerOverride,
    client.eerCalories,
    source.maintenanceEER,
    client.eer,
  ];
  const calories = candidates.find((candidate) => {
    const value = toNullableFiniteNumber(candidate);
    return value != null && value > 0;
  });

  const value = toNullableFiniteNumber(calories);
  return value != null && value > 0 ? Math.round(value) : null;
}

export function getClientDailyCalorieGoalPreview(client: Client | null | undefined): string {
  if (!client) return 'Not set';
  const calories = getClientDailyCalorieGoalKcal(client);
  return calories != null ? `${calories.toLocaleString()} kcal` : 'Not set';
}

function getClientGoalSummary(client: Client): string {
  const calories = getClientDailyCalorieGoalKcal(client);
  const clinicalAdjustment = getClientClinicalCalorieAdjustmentInfo(client);
  if (calories != null && clinicalAdjustment) {
    return `Daily Calorie Goal : ${calories.toLocaleString()} kcal (clinical adjustment from ${clinicalAdjustment.originalCalories.toLocaleString()} kcal)`;
  }
  return calories != null
    ? `Daily Calorie Goal : ${calories.toLocaleString()}`
    : 'Daily Calorie Goal : Not set';
}

function getDietaryPreferencesSummary(client: Client): string {
  const values = Array.isArray(client.dietaryPreferences) ? client.dietaryPreferences : [];
  const labels = values
    .map((value) => {
      const trimmed = String(value).trim();
      return getValidPreferences().find((preference) => preference.value.toLowerCase() === trimmed.toLowerCase())?.name || trimmed;
    })
    .filter(Boolean);

  return formatListPreview(labels);
}

function getAnthropometricItems(client: Client): DetailMetricItem[] {
  const measurementSystem = (client.preferredMeasurementSystem || 'metric') as 'metric' | 'imperial';
  const bmiValue = isPregnantFemaleClient(client) ? null : toNullableFiniteNumber(client.bmi);
  const bmiCategoryDisplay = client.bmiCategoryDisplay?.trim();
  const bmiDisplay = bmiValue != null && bmiValue > 0
    ? [bmiValue.toFixed(1), bmiCategoryDisplay].filter(Boolean).join(' · ')
    : 'Not set';
  const idealWeightKg = calculateIdealWeightKg(client.heightCm, client.bmi);
  const whr =
    Number(client.waistCircumferenceCm) > 0 && Number(client.hipCircumferenceCm) > 0
      ? (Number(client.waistCircumferenceCm) / Number(client.hipCircumferenceCm)).toFixed(2)
      : 'N/A';

  return [
    { label: 'BMI', value: bmiDisplay },
    { label: 'Waist Circumference', value: formatCircumference(client.waistCircumferenceCm, measurementSystem) },
    { label: 'Hip Circumference', value: formatCircumference(client.hipCircumferenceCm, measurementSystem) },
    { label: 'WHR', value: whr },
    { label: 'IBW', value: idealWeightKg != null ? formatWeight(idealWeightKg, measurementSystem) : 'Not set' },
    { label: 'Calculation Formula', value: getNutrientGuidelinesLabel(client) },
  ];
}

export function readClientHealthGoal(client: Client): string {
  const direct = client.healthGoal;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  return '';
}

function getActivityAndGoalItems(client: Client): DetailMetricItem[] {
  const measurementSystem = (client.preferredMeasurementSystem || 'metric') as 'metric' | 'imperial';
  const eerValue = toNullableFiniteNumber(client.eerOverride ?? client.eerCalories ?? client.eer);
  const pregnancyDetails = getPregnancyDetails(client);
  const sex = String(client.sexAtBirth || client.gender || '').toLowerCase();
  const weightGoalValue = client.weightGoal || client.lactationGoal;
  const healthGoalValue = readClientHealthGoal(client);
  const isLactating = Boolean(client.lactationPeriod) && client.lactationPeriod !== 'none';

  const items: DetailMetricItem[] = [
    { label: 'Health Goal', value: healthGoalValue || EMPTY_VALUE_DISPLAY },
    { label: 'Weight Goal', value: weightGoalValue ? toTitleCase(weightGoalValue) : EMPTY_VALUE_DISPLAY },
    { label: 'PAL', value: client.activityLevel ? toTitleCase(client.activityLevel) : EMPTY_VALUE_DISPLAY },
    { label: 'EER', value: eerValue != null && eerValue > 0 ? `${Math.round(eerValue).toLocaleString()}kcal/day` : EMPTY_VALUE_DISPLAY },
  ];

  if (sex === 'male') return items;

  const isPregnant = Boolean(pregnancyDetails.pregnancyStatus);
  const pregnancyItems: DetailMetricItem[] = [
    {
      label: 'Pregnancy Status',
      value: isPregnant ? pregnancyDetails.pregnancyStatus! : 'Not Pregnant',
    },
    {
      label: 'Pregnancy Trimester',
      value:
        isPregnant && pregnancyDetails.pregnancyTrimester && pregnancyDetails.pregnancyTrimester !== EMPTY_VALUE_DISPLAY
          ? pregnancyDetails.pregnancyTrimester
          : EMPTY_VALUE_DISPLAY,
    },
    {
      label: 'Pre-pregnancy Weight',
      value: client.prePregnancyWeightKg
        ? formatWeight(client.prePregnancyWeightKg, measurementSystem)
        : EMPTY_VALUE_DISPLAY,
    },
    {
      label: 'Lactation Status',
      value: formatLactationStatus(client.lactationPeriod),
    },
    {
      label: 'Pregnancy Type',
      value:
        isPregnant || isLactating
          ? (pregnancyDetails.pregnancyType || (client.isTwinPregnancy ? 'Twin Pregnancy' : 'Single Pregnancy'))
          : EMPTY_VALUE_DISPLAY,
    },
  ];

  return [...items, ...pregnancyItems];
}

function readNestedGoalValue(clientGoals: unknown, key: string, path: string[]): number | null {
  let value: unknown = toUnknownRecord(clientGoals)?.[key];
  for (const part of path) {
    value = toUnknownRecord(value)?.[part];
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getClientGoalRows(client: Client): ClientGoalRow[] {
  const drv = client.drvRequirements?.macronutrients;
  const findMacro = (groups: unknown, nutrientId: string) =>
    Array.isArray(groups)
      ? groups.find((item) => toUnknownRecord(item)?.nutrientId === nutrientId) ?? null
      : null;
  const proteinRef = findMacro(drv?.proteins, 'protein');
  const carbsRef = findMacro(drv?.carbohydrates, 'carbohydrate');
  const fibreRef = findMacro(drv?.carbohydrates, 'dietary_fibre');
  const fatRef = findMacro(drv?.lipids, 'total_fat');
  const satFatRef = findMacro(drv?.lipids, 'saturated_fat');

  const isLegacyGramOnlyAmdr = (ref: unknown) => {
    const row = toUnknownRecord(ref);
    const nutrientId = String(row?.nutrientId ?? '').trim().toLowerCase();
    const unit = String(row?.unit ?? '').trim().toLowerCase();
    const hasAmdrRange = row?.amdrLow != null && row?.amdrHigh != null;
    const hasSeparateGramRange = row?.amdrLowGrams != null || row?.amdrHighGrams != null;

    return nutrientId === 'total_fat'
      && (unit === 'g' || unit === 'g/day')
      && hasAmdrRange
      && !hasSeparateGramRange;
  };
  const pct = (ref: unknown) => {
    const row = toUnknownRecord(ref);
    // Legacy total-fat snapshots stored gram bounds in amdrLow/amdrHigh.
    // Do not label those gram values as percentages.
    if (isLegacyGramOnlyAmdr(ref)) return 'N/A';
    return row?.amdrLow != null && row?.amdrHigh != null ? `${row.amdrLow}%-${row.amdrHigh}%` : 'N/A';
  };
  const range = (ref: unknown) => {
    const row = toUnknownRecord(ref);
    if (isLegacyGramOnlyAmdr(ref)) {
      return `${row?.amdrLow}-${row?.amdrHigh}${String(row?.unit ?? 'g')}`;
    }
    const value = row?.rda ?? row?.ai ?? row?.ul ?? row?.amdrLowGrams;
    if (value == null) return 'N/A';
    const unit = row?.unit ? String(row.unit) : 'g/day';
    return `${value}${unit}`;
  };

  const rows: ClientGoalRow[] = [
    {
      nutrient: 'Protein',
      percentage: pct(proteinRef),
      referenceRange: range(proteinRef),
      grams: String(readNestedGoalValue(client.clientGoals, 'protein', ['grams', 'value']) ?? ''),
      kcal: String(readNestedGoalValue(client.clientGoals, 'protein', ['kcal', 'value']) ?? ''),
      unit: 'g',
    },
    {
      nutrient: 'Carbohydrate',
      percentage: pct(carbsRef),
      referenceRange: range(carbsRef),
      grams: String(readNestedGoalValue(client.clientGoals, 'carbohydrates', ['grams', 'value']) ?? ''),
      kcal: String(readNestedGoalValue(client.clientGoals, 'carbohydrates', ['kcal', 'value']) ?? ''),
      unit: 'g',
    },
    {
      nutrient: 'Fiber',
      percentage: 'N/A',
      referenceRange: range(fibreRef),
      grams: String(readNestedGoalValue(client.clientGoals, 'dietaryFibre', ['value']) ?? ''),
      unit: 'g/day',
    },
    {
      nutrient: 'Total Fat',
      percentage: pct(fatRef),
      referenceRange: range(fatRef),
      grams: String(readNestedGoalValue(client.clientGoals, 'totalFat', ['grams', 'value']) ?? ''),
      kcal: String(readNestedGoalValue(client.clientGoals, 'totalFat', ['kcal', 'value']) ?? ''),
      unit: 'g',
    },
    {
      nutrient: 'Sat Fat',
      percentage: pct(satFatRef),
      referenceRange: range(satFatRef),
      grams: String(readNestedGoalValue(client.clientGoals, 'saturatedFat', ['grams', 'value']) ?? ''),
      kcal: String(readNestedGoalValue(client.clientGoals, 'saturatedFat', ['kcal', 'value']) ?? ''),
      unit: 'g',
    },
  ];
  const calories = resolveClientTargetCalories(client);
  const limits = (client.clientGoals as any)?.clinicalRules?.resolvedTargets?.limits;
  const keys: Record<string, string> = { Protein: 'protein', Carbohydrate: 'carbs', 'Total Fat': 'fat', 'Sat Fat': 'saturatedFat', Fiber: 'fibre' };
  return rows.map(row => ({
    ...row,
    percentage: row.kcal && calories ? `${macroEnergyPercentage(Number(row.kcal), calories).toFixed(1)}%` : 'N/A',
    referenceRange: formatStructuredClinicalLimit(limits?.[keys[row.nutrient]]) ?? row.referenceRange,
  }));
}

function getSymptomItems(client: Client): DetailMetricItem[] {
  const extendedClient = client as Client & {
    reportedSymptoms?: string[] | string | null;
    symptoms?: string[] | string | null;
  };
  const rawSymptoms = extendedClient.reportedSymptoms ?? extendedClient.symptoms;
  let symptoms = '';

  if (Array.isArray(rawSymptoms)) {
    symptoms = rawSymptoms.filter(Boolean).join(', ');
  } else if (rawSymptoms && typeof rawSymptoms === 'object') {
    symptoms = Object.entries(rawSymptoms)
      .filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'boolean') return value;
        return value !== null && value !== undefined && String(value).trim() !== '';
      })
      .map(([key, value]) => {
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'boolean') return toTitleCase(key);
        return String(value);
      })
      .filter(Boolean)
      .join(', ');
  } else {
    symptoms = String(rawSymptoms ?? '').trim();
  }

  return [{ label: 'Reported Symptoms', value: symptoms || 'None' }];
}

function getQuestionnaireCompletedLabel(completedQuestionnaires: number): string {
  return completedQuestionnaires === 1 ? '1 Complete' : `${completedQuestionnaires} Completed`;
}

function getQuestionnaireProgressLabel(completedQuestionnaires: number): string {
  return completedQuestionnaires === 1
    ? '1/4 Complete'
    : `${completedQuestionnaires}/4 Completed`;
}

function normalizeClientsListStatus(status: string | null): string | null {
  return status === 'all' || status === 'active' || status === 'suspended' || status === 'draft' || status === 'pending_deletion'
    ? status
    : null;
}

function buildClientsBackPath(clientsQueryString: string): string {
  if (!clientsQueryString) return '/clients';

  const params = new URLSearchParams(clientsQueryString);
  const hasFromParams =
    params.has('fromPage') || params.has('fromLimit') || params.has('fromStatus') || params.has('fromView');

  if (!hasFromParams) {
    if (!normalizeClientsListStatus(params.get('status'))) {
      params.delete('status');
    }
    const sanitizedQuery = params.toString();
    return sanitizedQuery ? `/clients?${sanitizedQuery}` : '/clients';
  }

  const backParams = new URLSearchParams();
  const status = normalizeClientsListStatus(params.get('fromStatus') ?? params.get('status'));
  const page = params.get('fromPage') ?? params.get('page');
  const limit = params.get('fromLimit') ?? params.get('limit');
  const view = params.get('fromView') ?? params.get('view');

  if (status) backParams.set('status', status);
  if (page) backParams.set('page', page);
  if (limit) backParams.set('limit', limit);
  if (view === 'list') backParams.set('view', 'list');

  const query = backParams.toString();
  return query ? `/clients?${query}` : '/clients';
}

export function buildActiveSubscriptionSummary(
  activeSubscription: ClientActiveSubscription | null | undefined
): ActiveSubscriptionSummary | null {
  if (!activeSubscription) return null;

  const periodLabel = `${formatDate(activeSubscription.startDate, 'd MMM yyyy', EMPTY_CLIENT_DETAIL_VALUE)} – ${formatDate(activeSubscription.endDate, 'd MMM yyyy', EMPTY_CLIENT_DETAIL_VALUE)}`;
  const servicesLabel =
    activeSubscription.lineItems
      .map((item) => item.description?.trim())
      .filter(Boolean)
      .join(', ') || 'Active subscription';

  return {
    periodLabel,
    servicesLabel,
    statusLabel: toTitleCase(activeSubscription.status),
  };
}

export function buildClientDetailsV2ViewModel(
  client: Client,
  clientsQueryString: string,
  activeSubscription?: ClientActiveSubscription | null
): ClientDetailsV2ViewModel {
  const age = getAge(client.dateOfBirth);
  const genderLabel = client.gender ? toTitleCase(client.gender) : EMPTY_CLIENT_DETAIL_VALUE;
  const sexAtBirthLabel = client.sexAtBirth ? toTitleCase(client.sexAtBirth) : EMPTY_CLIENT_DETAIL_VALUE;
  const phoneLabel = formatPhoneDisplay(client.phoneCountryCode, client.phone);
  const dateOfBirthLabel = client.dateOfBirth
    ? `${formatDate(client.dateOfBirth, 'dd MMM yyyy', EMPTY_CLIENT_DETAIL_VALUE)}${age != null ? ` (${age})` : ''}`
    : EMPTY_CLIENT_DETAIL_VALUE;

  const extendedClient = client as Client & {
    occupation?: string | null;
    emergencyContact?: string | null;
    emergencyContactPhone?: string | null;
    emergencyContactNumber?: string | null;
  };
  const occupationLabel = extendedClient.occupation?.trim() || EMPTY_CLIENT_DETAIL_VALUE;
  const emergencyContactLabel =
    extendedClient.emergencyContactPhone?.trim() ||
    extendedClient.emergencyContactNumber?.trim() ||
    extendedClient.emergencyContact?.trim() ||
    EMPTY_CLIENT_DETAIL_VALUE;

  const conditionNames = getMedicalConditionNames(client);
  const pregnancyDetails = getPregnancyDetails(client);
  const allergies = Array.isArray(client.allergies) ? client.allergies : [];
  const allergyValues = Array.from(new Set(allergies.map((item) => {
    const trimmed = item.trim();
    // Map legacy allergy values
    if (trimmed === 'sulfites') return 'sulfite';
    return trimmed;
  }).filter(Boolean)));
  const allergyDisplayNames = allergyValues.map(val => getValidAllergies().find(a => a.value.toLowerCase() === val.toLowerCase())?.name || formatDisplayLabel(val));

  const intolerances = Array.isArray(client.foodIntolerances)
    ? client.foodIntolerances
    : Array.isArray(client.dietaryPreferences)
      ? client.dietaryPreferences
      : [];
  const intoleranceValues = Array.from(new Set(intolerances.map((item) => {
    const trimmed = item.trim();
    // Map legacy intolerance values
    if (trimmed === 'gluten_non_coeliac') return 'gluten';
    if (trimmed === 'fodmaps') return 'fodmap';
    return trimmed;
  }).filter(Boolean)));
  const intoleranceDisplayNames = intoleranceValues.map(val => {
    const foundIntolerance = getValidFoodIntolerances().find(i => i.value.toLowerCase() === val.toLowerCase());
    if (foundIntolerance) return foundIntolerance.name;
    const foundPreference = getValidPreferences().find(p => p.value.toLowerCase() === val.toLowerCase());
    if (foundPreference) return foundPreference.name;
    return formatDisplayLabel(val);
  });

  const completedQuestionnaires = Array.isArray(client.preConsultationMedicalConditions)
    ? client.preConsultationMedicalConditions.length
    : 0;
  const nutrientGuidelinesSummary = getNutrientGuidelinesSummary(client);
  const clientGoalSummary = getClientGoalSummary(client);
  const countryLabel = client.location ? formatDisplayLabel(client.location) : EMPTY_CLIENT_DETAIL_VALUE;

  const clientDisplayName = client.fullName || `${client.firstName} ${client.lastName}`.trim() || 'Client';

  return {
    clientDisplayName,
    emailDisplay: client.email || 'Email not set',
    clientsBackPath: buildClientsBackPath(clientsQueryString),
    /** Real next-appointment time should come from overview/appointments APIs; avoid placeholder timestamps. */
    nextAppointmentDate: null,
    statusLabel:
      client.status === 'pending_deletion'
        ? 'Pending deletion'
        : client.status === 'active'
        ? 'Active profile'
        : client.status === 'inactive' || client.status === 'suspended' || client.status === 'archived'
          ? `${toTitleCase(client.status)} profile`
          : 'Active profile',
    aboutDetails: {
      fullName: clientDisplayName,
      dateOfBirth: dateOfBirthLabel,
      gender: genderLabel,
      sexAtBirth: sexAtBirthLabel,
      phone: phoneLabel,
      email: client.email || EMPTY_CLIENT_DETAIL_VALUE,
      location: client.location || EMPTY_CLIENT_DETAIL_VALUE,
      cityOrCounty: client.cityOrCounty || EMPTY_CLIENT_DETAIL_VALUE,
      occupation: occupationLabel,
      emergencyContact: emergencyContactLabel,
      pregnancyStatus: pregnancyDetails.pregnancyStatus,
      pregnancyTrimester: pregnancyDetails.pregnancyTrimester,
      pregnancyType: pregnancyDetails.pregnancyType,
    },
    allergies: allergyValues,
    intolerances: intoleranceValues,
    summaryItems: [
      {
        title: 'About',
        value: `${age != null ? `${age} Years` : 'Age N/A'}, ${genderLabel}, ${countryLabel}`,
        valueParts: [age != null ? `${age} Years` : 'Age N/A', genderLabel, countryLabel],
      },
      { title: 'Allergies', value: formatListPreview(allergyDisplayNames) },
      { title: 'Intolerances', value: formatListPreview(intoleranceDisplayNames) },
      { title: 'Nutrient Guidelines', value: nutrientGuidelinesSummary },
    ],
    metrics: getMetricItems(client),
    anthropometricItems: getAnthropometricItems(client),
    activityAndGoalItems: getActivityAndGoalItems(client),
    symptomItems: getSymptomItems(client),
    dietaryPreferencesSummary: getDietaryPreferencesSummary(client),
    clientGoalSummary,
    nutrientGuidelinesSummary,
    clientGoalRows: getClientGoalRows(client),
    goals: getGoals(client),
    conditionNames,
    biomarkerItems: client.biomarkers ?? [],
    completedQuestionnaires,
    questionnaireCompletedLabel: getQuestionnaireCompletedLabel(completedQuestionnaires),
    questionnaireProgressLabel: getQuestionnaireProgressLabel(completedQuestionnaires),
    activeSubscriptionSummary: buildActiveSubscriptionSummary(activeSubscription),
  };
}
